import React, { CSSProperties, useEffect, useRef } from 'react';
import gsap from 'gsap';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { SEAT_CONTAINER_HEIGHT } from 'constants/gameConstants';
import { seatCardBandReach } from 'components/Seat/SeatWithPlayer/SeatCards';
import { SeatPosition } from 'hooks/useEllipticalPositions';
import { TableZ } from 'styles/zIndex';

type ReturnedBetMessagePropType = {
  /** Seat the message should anchor to (the over-bettor's seat). */
  at: SeatPosition;
  /** Hole cards sit higher above the seat in landscape, so the lift differs. */
  isPortrait: boolean;
};

/**
 * Fades in a small "Uncalled bet returned" label near the over-bettor's seat,
 * synced with the ReturnedBetChips animation. Disappears after a beat so it
 * doesn't linger into the showdown reveal.
 *
 * Mount with a fresh key each over-bet so the animation re-fires.
 */
const ReturnedBetMessage = ({ at, isPortrait }: ReturnedBetMessagePropType) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;
    // Delay matches ReturnedBetChips burst (~0.18s) so the label fades in
    // just as the chips begin their flight.
    const tl = gsap.timeline();
    tl.fromTo(
      ref.current,
      { opacity: 0, scale: 0.85, y: -4 },
      { opacity: 1, scale: 1, y: 0, duration: 0.3, delay: 0.18, ease: 'back.out(1.5)' }
    )
      .to(ref.current, { opacity: 0, duration: 0.4, ease: 'power1.in' }, '+=1.4');

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <div
      style={{
        ...containerStyle,
        left: `${at.leftPct}%`,
        top: `${at.topPct}%`,
        transform: `translate(-50%, calc(-100% - ${seatTopReachPx(isPortrait)}px * var(--seat-scale, 1)))`
      }}>
      <div ref={ref} style={bannerStyle}>
        <UndoRoundedIcon style={{ fontSize: '0.95rem', color: '#e9b43c' }} />
        <span style={textStyle}>Uncalled bet</span>
      </div>
    </div>
  );
};

export default ReturnedBetMessage;

/**
 * How far above the seat's CENTRE its hole cards reach, in unscaled px.
 *
 * The banner used to clear the anchor by 0.6rem, which put it straight over
 * the card band — and it was on a lower z band, so it painted behind the cards
 * rather than on them (reported 2026-09-04). Clearing the cards outright is
 * the fix; the airspace above them is otherwise empty felt.
 *
 * Multiplied by `--seat-scale` at the call site because the seat and its cards
 * are painted through that transform while this banner is not — a fixed px
 * offset would drift the moment the table shrank.
 *
 * TAKES THE TALLER OF FACE-DOWN AND FACE-UP — `seatCardBandReach`, shared with
 * the seat's action bubble, which had the same bug for the same reason. It used
 * to measure the face-DOWN band only, which understates a revealed hand by ~10px
 * in landscape, so the banner sat inside the top of the cards — and it is banded
 * ABOVE every seat (TableZ.returnedBetMessage), so it covered them rather than
 * slipping behind. An uncalled bet and a reveal land on the same seat whenever
 * the last aggressor is called and then shown, which is why this was reported as
 * "the uncalled-bet pill covers my hole cards".
 */
const seatTopReachPx = (isPortrait: boolean): number =>
  SEAT_CONTAINER_HEIGHT / 2 + seatCardBandReach(isPortrait) + RETURNED_BET_GAP_PX;

/** Air between the top of the hole cards and the banner above them. */
const RETURNED_BET_GAP_PX = 6;

const containerStyle: CSSProperties = {
  position: 'absolute',
  // Above every seat — see TableZ.returnedBetMessage for why.
  zIndex: TableZ.returnedBetMessage,
  pointerEvents: 'none'
};

const bannerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.35rem',
  padding: '0.25rem 0.6rem',
  backgroundColor: 'rgba(0, 0, 0, 0.78)',
  borderRadius: '999px',
  border: '1px solid rgba(233, 180, 60, 0.35)',
  boxShadow: '0 4px 14px rgba(0, 0, 0, 0.45)',
  whiteSpace: 'nowrap',
  opacity: 0
};

const textStyle: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  color: '#ffffff',
  letterSpacing: '0.02em',
  fontFamily: "'Outfit', sans-serif"
};
